// SignupWizard.tsx
import React, { useState, useEffect } from 'react';
import SignupStepOne from './SignupStepOne';
import StepIndicator from './StepIndicator';
import PersonInfo from './PersonInfo';
import SubscriptionCompleted from './SubscriptionCompleted';
import TermsConditions from "./TermsConditionsProps"; // Ensure correct import path
import { instance } from '../../../api/axiosInstance';

interface SignupData {
  email: string;
  isEmailVerified: boolean;
  personalInfo: {
    name: string;
    nickname: string;
    birthDate: string;
    phone: string | undefined;
    username: string;
    password: string;
    confirmPassword: string;
    email: string;
    emailDomain: string;
    referralCode: string;
    marketingSMS: boolean;
    marketingEmail: boolean;
    marketingDM: boolean;
    marketingTM: boolean;
  };
}

interface MarketingMethod {
  id: number;
  name: string;
}

function SignupWizard() {
  const [step, setStep] = useState(1);
  const [signupData, setSignupData] = useState<SignupData>({
    email: '',
    isEmailVerified: false,
    personalInfo: {
      name: '',
      nickname: '',
      birthDate: '',
      phone: '',
      username: '',
      password: '',
      confirmPassword: '',
      email: '',
      emailDomain: '직접입력',
      referralCode: '',
      marketingSMS: false,
      marketingEmail: false,
      marketingDM: false,
      marketingTM: false,
    },
  });
  const [marketingMethods, setMarketingMethods] = useState<MarketingMethod[]>([]); // Marketing methods state
  const [errors, setErrors] = useState<string[]>([]); // Error messages state

  useEffect(() => {
    const fetchMarketingMethods = async () => {
      try {
        const response = await instance.get('/api/users/marketing-methods');
        setMarketingMethods(response.data);
      } catch (error) {
        console.error('Failed to fetch marketing methods:', error);
      }
    };
    fetchMarketingMethods();
  }, []);

  const nextStep = () => {
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleEmailVerify = (email: string) => {
    setSignupData((prev) => ({
      ...prev,
      email,
      isEmailVerified: true,
      personalInfo: {
        ...prev.personalInfo,
        email, // Save email from Step 1 to personalInfo
      },
    }));
    nextStep();
  };

  const handleTermsConsents = (consents: { [key: string]: boolean }) => {
    // Handle consents if needed
    nextStep();
  };

  // Updated to handle form data from PersonInfo
  const handlePersonalInfo = async (personalInfo: Partial<SignupData['personalInfo']>) => {
    // Compute the updated signup data
    const updatedSignupData: SignupData = {
      ...signupData,
      personalInfo: {
        ...signupData.personalInfo,
        ...personalInfo,
      },
    };

    // Update the state with the new data
    setSignupData(updatedSignupData);

    // Call completeSignup with the updated data
    await completeSignup(updatedSignupData);
  };

  const completeSignup = async (data: SignupData) => {
    try {
      // Map selected marketing methods to their IDs
      const marketingMethodIds = marketingMethods
          .filter((method) => {
            switch (method.name) {
              case 'SMS':
                return data.personalInfo.marketingSMS;
              case 'Email':
                return data.personalInfo.marketingEmail;
              case 'DM':
                return data.personalInfo.marketingDM;
              case 'TM':
                return data.personalInfo.marketingTM;
              default:
                return false;
            }
          })
          .map((method) => method.id);

      // Determine if any marketing consent is given
      const marketingConsent = marketingMethodIds.length > 0;

      // Prepare the payload
      const payload = {
        username: data.personalInfo.username,
        fullName: data.personalInfo.name,
        birthDate: data.personalInfo.birthDate,
        phone: data.personalInfo.phone,
        password: data.personalInfo.password,
        confirmPassword: data.personalInfo.confirmPassword,
        email: data.personalInfo.email,
        emailDomain: data.personalInfo.emailDomain,
        referralCode: data.personalInfo.referralCode,
        marketingConsent: marketingConsent,
        marketingMethodIds: marketingMethodIds,
        nickname: data.personalInfo.nickname,
      };

      // Make the signup API call
      const response = await instance.post('/api/users/register', payload);

      console.log('회원가입 성공:', response.data);
      // Move to the next step upon successful signup
      nextStep();
    } catch (error: any) {
      console.error('회원가입 실패:', error.response?.data || error.message);

      if (error.response && Array.isArray(error.response.data)) {
        // If server returns an array of errors
        const errorMessages = error.response.data.map((err: any) => err.defaultMessage);
        setErrors(errorMessages);
      } else if (error.response && error.response.data && error.response.data.message) {
        // If server returns a single error message
        setErrors([error.response.data.message]);
      } else {
        // Other errors
        setErrors(['알 수 없는 오류가 발생했습니다.']);
      }

      // Optionally, you can navigate back or handle errors as needed
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <SignupStepOne onEmailVerify={handleEmailVerify} />;
      case 2:
        return (
            <TermsConditions
                onAdvance={handleTermsConsents}
                onGoBack={prevStep}
            />
        );
      case 3:
        return (
            <PersonInfo
                onComplete={handlePersonalInfo} // Pass the updated handlePersonalInfo
                onGoBack={prevStep}
                preFilledEmail={signupData.personalInfo.email}
            />
        );
      case 4:
        return <SubscriptionCompleted onGoBack={prevStep} />;
      default:
        return <SignupStepOne onEmailVerify={handleEmailVerify} />;
    }
  };

  return (
      <div className="min-h-screen h-full bg-gray-100 mobile:px-4 flex flex-col items-center justify-center">
        <StepIndicator currentStep={step} />
        {/*{errors.length > 0 && (*/}
        {/*    <div className="w-full max-w-lg bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">*/}
        {/*      <ul>*/}
        {/*        {errors.map((errorMsg, index) => (*/}
        {/*            <li key={index}>• {errorMsg}</li>*/}
        {/*        ))}*/}
        {/*      </ul>*/}
        {/*    </div>*/}
        {/*)}*/}
        {renderStep()}
      </div>
  );
}

export default SignupWizard;
